import { ColumnState } from 'ag-grid-community'
import { useCallback, useEffect, useMemo, useState } from 'react'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import {
  BulkSheetColumnAndFilterState,
  BulkSheetViewColumnAndFilterState,
} from '../../../model/bulkSheetColumnAndFilterState'
import { useColumnAndFilterStateStorageService } from '../../../../services/storage-services/ui-states/columnAndFilterStateStorageService'
import { getUrlQueryObject } from '../../../../utils/urls'
import { URL_PARAMS_SHARE_SAVED_UI_STATE } from '../../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'

export const useColumnAndFilterState = (
  searchConditionStateInited: boolean,
  functionUuid: string,
  projectUuid: string,
  ticketType: string | undefined,
  ticketListUuid: string | undefined
) => {
  const [initialized, setInitialized] = useState(false)
  const [columnState, setColumnState] = useState<ColumnState[]>([])
  const [filterState, setFilterState] = useState<{ [key: string]: any }>({})
  const [restored, setRestored] = useState(false)

  const uiStateKey = useMemo(() => {
    const base = `${UiStateKey.TicketColumnAndFilterState}-${projectUuid}`
    return ticketType && ticketListUuid
      ? `${base}-${ticketType}-${ticketListUuid}`
      : base
  }, [projectUuid, ticketType, ticketListUuid])

  const columnAndFilterStateStorageService =
    useColumnAndFilterStateStorageService(
      functionUuid,
      projectUuid || '',
      ticketType
    )

  const restoreSavedUiState = useCallback(async (): Promise<
    BulkSheetViewColumnAndFilterState | undefined
  > => {
    if (!projectUuid) return
    const urlObject = getUrlQueryObject()
    if (!urlObject) return undefined
    const code =
      URL_PARAMS_SHARE_SAVED_UI_STATE in urlObject
        ? urlObject[URL_PARAMS_SHARE_SAVED_UI_STATE]
        : undefined
    if (!code) return undefined
    const permanentUiState =
      await columnAndFilterStateStorageService.getByCode(code)
    if (!permanentUiState) return undefined
    return permanentUiState.value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUuid, uiStateKey, columnAndFilterStateStorageService.getByCode])

  const restorePageState = useCallback(async () => {
    const savedUiState = await restoreSavedUiState()
    try {
      if (savedUiState) {
        savedUiState.columnState && setColumnState(savedUiState.columnState)
        savedUiState.filterState && setFilterState(savedUiState.filterState)
        return
      }
      const response = await uiStates.get({
        applicationFunctionUuid: functionUuid,
        key: uiStateKey,
        scope: UiStateScope.User,
      })
      const stored = response.json
      if (!stored.value) {
        return
      }
      const payload: BulkSheetColumnAndFilterState = JSON.parse(stored.value)
      payload.column && setColumnState(payload.column)
      payload.filter && setFilterState(payload.filter)
    } finally {
      setInitialized(true)
    }
  }, [functionUuid, uiStateKey, restoreSavedUiState])

  const rememberPageState = useCallback(
    (value: BulkSheetColumnAndFilterState) => {
      uiStates.update(
        {
          key: uiStateKey,
          scope: UiStateScope.User,
          value: JSON.stringify(value),
        },
        functionUuid
      )
    },
    [functionUuid, uiStateKey]
  )

  const saveImmediately = useCallback(() => {
    rememberPageState({
      column: columnState,
      filter: filterState,
    })
  }, [columnState, filterState, rememberPageState])

  const saveColumnState = useCallback(
    (s: ColumnState[]) => {
      if (!restored) return
      setColumnState(s)
    },
    [restored]
  )

  const saveFilterState = useCallback(
    s => {
      if (!restored) return
      setFilterState(s)
    },
    [restored]
  )

  const startRestoring = useCallback(() => setRestored(false), [])
  const finishRestoring = useCallback(() => setRestored(true), [])

  useEffect(() => {
    if (!searchConditionStateInited) return
    restorePageState()
  }, [searchConditionStateInited, restorePageState])

  useEffect(() => {
    if (!initialized) return
    if (!restored) return
    saveImmediately()
  }, [initialized, restored, saveImmediately])

  const result = useMemo(
    () => ({
      initialized,
      startRestoring,
      finishRestoring,
      columnState,
      saveColumnState,
      filterState,
      saveFilterState,
      saveImmediately,
    }),
    [
      initialized,
      startRestoring,
      finishRestoring,
      columnState,
      saveColumnState,
      filterState,
      saveFilterState,
      saveImmediately,
    ]
  )

  return result
}
